import { getAlertsSummaryDataWithPagination } from '@src/api/aiAlerts';
import { dimensionsListSelectorKeyValue } from '@src/store/common/dimensionList/selectors';
import { aiAlertsDimensionMappingSelector } from '@src/store/common/selectors';
import { CURRENT_PRODUCT_MODE } from '@src/utils/constants';
import { get } from 'lodash';
import moment from 'moment-timezone';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { formatAIAlertSummary } from '../formatter';
import { AlertsPage, InfiniteScrollFilterParams } from '../types';
import { QUERY_SORT_MAPPER, SORT_ORDER_MAPPER } from '../constants';

// ❗❗ fetchAll=true should be used very carefully
export const useFetchAlertsWithPagination = (
  interval,
  filterParams: InfiniteScrollFilterParams,
  fetchAll: boolean = false,
  sortParams: { key: string; order: string } = { key: 'eventTime', order: 'descend' },
) => {
  const { from, to } = interval;
  const { key, order } = sortParams;
  const dimensionListObject = useSelector(dimensionsListSelectorKeyValue);
  const dimensionMapping = useSelector(aiAlertsDimensionMappingSelector);
  // const { autoInsightsFilter } = useAiAlertStore((state) => ({
  //   autoInsightsFilter: state.autoInsightsFilter,
  // }));

  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, isError, refetch } =
    useInfiniteQuery(
      ['ai-alerts-infinite', from, to, JSON.stringify(filterParams), JSON.stringify(sortParams)], // Include sortParams in query key for proper cache invalidation
      ({ pageParam = null }) => {
        // Remove signal for now

        const { searchText, alertId, rootCause, metricType, severity, status } = filterParams;
        const requestBody = {
          from: moment(from).format(),
          to: moment(to).format(),
          limit: filterParams.limit,
          cursor: pageParam, // Backend cursor for pagination
          cache: true,
          filters: {
            // ...(alertId && { alertId: alertId }), // not required in summary table
            // ...(rootCause && { rootCause: rootCause }), // not required in summary table
            // Handle multiple metric types (OR within group)
            ...(metricType && {
              metricType: Array.isArray(metricType) ? metricType : [metricType],
            }),
            // Handle multiple severities (OR within group)
            ...(severity && {
              severity: Array.isArray(severity) ? severity : [severity],
            }),
            // Handle multiple statuses (OR within group)
            ...(status && {
              status: status, // BE expects status to be in ('all', 'ended', 'active')
            }),
            // Include auto insights filter if present
            // ...(autoInsightsFilter.alertIds?.length && { alertIds: autoInsightsFilter.alertIds }), // auto insights not supported in DPI
          },
          search: {
            ...(searchText &&
              (!isNaN(Number(searchText)) && searchText.trim() !== ''
                ? {
                    alertId: searchText,
                    rootCauseDisplay: searchText,
                    dimensionNameDisplay: searchText,
                    metricNameDisplay: searchText,
                  }
                : {
                    rootCauseDisplay: searchText,
                    dimensionNameDisplay: searchText,
                    metricNameDisplay: searchText,
                  })),
          },
          sortBy: QUERY_SORT_MAPPER[key],
          sortOrder: SORT_ORDER_MAPPER[order],
          includeCounts: true, // Re-enable counts for optimization
          getAll: fetchAll, // only use when absolutely needed, else avoid, specially for longer date range
        };

        return getAlertsSummaryDataWithPagination(requestBody).then((response) => {
          const responseData = get(response, ['data'], {});
          // Alerts are in responseData.data (as per user clarification)
          const alerts = get(responseData, ['data'], []);
          const pagination = get(responseData, ['pagination'], {});

          const totalCount = get(pagination, ['totalCount'], 0);
          const nextCursor = get(pagination, ['nextCursor'], null);
          const hasMore = Boolean(get(pagination, ['hasMore'], false));
          const limit = get(pagination, ['limit'], 0);
          const cacheKey = get(pagination, ['cacheKey'], null);

          // Extract filter counts from the response
          const counts = get(responseData, ['totals'], {});
          const metricCounts = get(counts, ['metricCounts'], {});

          const formattedAlerts = alerts?.length
            ? formatAIAlertSummary(
                alerts,
                dimensionListObject as any,
                dimensionMapping[CURRENT_PRODUCT_MODE.toLowerCase()],
              )
            : [];

          return {
            alerts: formattedAlerts,
            nextCursor,
            hasNextPage: hasMore,
            total: totalCount,
            limit,
            cacheKey,
            counts, // Include counts in the response
            metricCounts, // Include metric counts separately for easier access
          } as AlertsPage;
        });
      },
      {
        getNextPageParam: (lastPage) => {
          // Accept 0 as a valid cursor (if backend uses 0 as a valid value)
          if (
            lastPage.hasNextPage &&
            lastPage.nextCursor !== null &&
            lastPage.nextCursor !== undefined
          ) {
            return lastPage.nextCursor;
          }
          return undefined;
        },
        refetchOnWindowFocus: false,
        retry: false,
        staleTime: 30 * 1000, // 30 seconds stale time to prevent rapid refetches
        cacheTime: 5 * 60 * 1000, // Keep cache for 5 minutes
        enabled: Boolean(interval?.from && interval?.to), // More explicit enabled condition
        keepPreviousData: false, // Don't keep previous data to prevent race condition display
        refetchOnMount: false, // Don't always refetch on mount - use cache when appropriate
        // Better performance settings for infinite scroll
        notifyOnChangeProps: ['data', 'error', 'isLoading', 'isFetchingNextPage'], // Only notify on relevant changes
        structuralSharing: true, // Re-enable for better performance
      },
    );

  // Flatten all pages into a single array
  const allAlerts = data?.pages?.flatMap((page: AlertsPage) => page.alerts) || [];
  const total = (data?.pages?.[0] as AlertsPage)?.total || 0;
  const counts = (data?.pages?.[0] as AlertsPage)?.counts || {};

  return {
    alerts: allAlerts,
    total,
    counts, // Include counts for sidebar
    hasNextPage: hasNextPage || false,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    isError,
    refetch,
    isEmpty: allAlerts.length === 0,
  };
};
